import { useTranslations } from "@zapier/i18n";
import { HydratedTable } from "@zapier/client";

import {
  Button,
  Field,
  ModalDialogBody,
  ModalDialogFooter,
  ModalDialogHeader,
  ModalDialogRoot,
  TextInput,
} from "@zapier/design-system";

import { useForm } from "react-hook-form";
import { useEffect, useMemo, useRef } from "react";
import { getMessageFromNetworkError, useRenameTable } from "@zapier/client";
import { useNotification } from "@zapier/utils";
import styles from "./TableRenameModal.module.css";
import { useEmitTableRenameModalEvent } from "@zapier/shared/src/events";
import { useAutoSelectInputText } from "@zapier/shared";

interface Props {
  table: HydratedTable | undefined;
  onCloseModal: () => void;
  isOpen: boolean;
  folderId?: string;
}

interface FormData {
  name: string;
  description: string | undefined;
}

interface ModalContentProps {
  defaultValues: FormData;
  onCloseModal: () => void;
  onSubmit: (data: FormData) => void;
  isOpen: boolean;
}

function TableRenameModalInternal({
  defaultValues,
  onCloseModal,
  onSubmit,
  isOpen,
}: ModalContentProps) {
  const t = useTranslations("table-rename-modal");
  const nameInputIdentifier = "rename-table-name";

  const {
    register,
    handleSubmit,
    formState: { isSubmitting, isDirty, isValid },
    reset,
  } = useForm<FormData>({
    defaultValues,
  });

  const { emit } = useEmitTableRenameModalEvent();

  // Resync the form to the table's values in two cases:
  //  - On every closed -> open transition, UNCONDITIONALLY. Reopening is
  //    exactly when a fresh start is wanted, so this must fire even when the
  //    form is dirty from a previous, cancelled edit (the useForm state lives
  //    in this always-mounted component and persists across open/close).
  //  - While already open, only when NOT dirty — so an unrelated parent
  //    re-render (e.g. a window-focus refetch of the tables list) can pick up
  //    a new table identity without clobbering the user's in-progress input.
  // Because defaultValues is memoized on the table identity, a plain re-render
  // while open with the same table changes nothing here. See ENT-541.
  const wasOpen = useRef(isOpen);
  useEffect(() => {
    const justOpened = isOpen && !wasOpen.current;
    wasOpen.current = isOpen;

    if (justOpened) {
      reset(defaultValues);
      return;
    }

    if (!isDirty) {
      reset(defaultValues);
    }
  }, [isOpen, isDirty, defaultValues, reset]);

  useAutoSelectInputText({
    isOpen,
    inputSelector: `#${nameInputIdentifier}`,
    value: defaultValues.name,
  });

  return (
    <ModalDialogRoot
      isOpen={isOpen}
      onOpenChange={() => {
        emit("cancel");
        onCloseModal();
      }}
    >
      <form
        onSubmit={handleSubmit((data) => {
          emit("confirm");
          onSubmit(data);
        })}
      >
        <ModalDialogHeader>
          {t("header.rename-table", { tableName: defaultValues.name })}
        </ModalDialogHeader>
        <div className={styles.bodyContainer}>
          <ModalDialogBody>
            <div className={styles.bodyContent}>
              <Field
                label={t("fields.name.label")}
                inputId={nameInputIdentifier}
                isRequired
                renderInput={(inputProps) => (
                  <TextInput
                    {...inputProps}
                    {...register("name", { required: true })}
                    // eslint-disable-next-line jsx-a11y/no-autofocus -- intentionally focus the input when the modal opens
                    autoFocus
                    id={nameInputIdentifier}
                    defaultValue={defaultValues.name}
                    placeholder={t("fields.name.placeholder")}
                  />
                )}
              />
              <Field
                label={t("fields.description.label")}
                renderInput={(inputProps) => (
                  <TextInput
                    {...inputProps}
                    {...register("description")}
                    defaultValue={defaultValues.description}
                    placeholder={t("fields.description.placeholder")}
                  />
                )}
              />
            </div>
          </ModalDialogBody>
        </div>
        <ModalDialogFooter>
          <Button
            variant="tertiary"
            type="button"
            onPress={() => {
              emit("cancel");
              onCloseModal();
            }}
          >
            {t("buttons.cancel")}
          </Button>
          <Button
            type="submit"
            isDisabled={!isDirty || !isValid || isSubmitting}
          >
            {t("buttons.save")}
          </Button>
        </ModalDialogFooter>
      </form>
    </ModalDialogRoot>
  );
}

export default function TableRenameModal({
  table,
  onCloseModal,
  folderId,
  isOpen,
}: Props) {
  const t = useTranslations("table-rename-modal");
  // Memoize so the reference only changes when the underlying table actually
  // changes, not on every parent re-render. A stable reference keeps the
  // resync effect below from firing (and clobbering unsaved input) when the
  // parent re-renders for unrelated reasons. See ENT-541.
  const defaultValues = useMemo(
    () => ({
      name: table?.name ?? "",
      description: table?.description ?? "",
    }),
    [table?.id, table?.name, table?.description],
  );

  const { rename } = useRenameTable({ folderId });
  const { createNotification } = useNotification();
  const handleSubmit = ({ name, description }: FormData) => {
    if (!table) {
      return;
    }

    rename(
      {
        name,
        description,
        tableId: table.id,
      },
      {
        onSuccess: () => {
          createNotification({
            message: t("notifications.rename-success", { tableName: name }),
            type: "success",
          });
        },
        onError: (error) => {
          createNotification({
            title: t("notifications.rename-error-title", {
              tableName: table.name,
            }),
            message: getMessageFromNetworkError(error),
            type: "error",
          });
        },
      },
    );

    onCloseModal();
  };

  return (
    <TableRenameModalInternal
      defaultValues={defaultValues}
      onCloseModal={onCloseModal}
      onSubmit={handleSubmit}
      isOpen={isOpen}
    />
  );
}
